import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FileText,
  Edit3,
  Trash2,
  Plus,
  Eye,
  EyeOff,
  Search,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import "./BlogListPage.css";

const API_BASE = "https://revexia.ai/api";

function BlogListPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null); // post id to confirm delete
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const getToken = () => localStorage.getItem("access_token") || "";

  const fetchPosts = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const token = getToken();
      const response = await axios.get(`${API_BASE}/blog/`, {
        params: { published_only: false, limit: 100, skip: 0 },
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setPosts(response.data);
    } catch (err) {
      console.error("[BlogListPage] fetchPosts failed", err);
      setError("Failed to load blog posts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ Fetch all blogs (published + drafts)
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // ✅ Delete post
  const handleDelete = async (postId) => {
    setDeleteLoading(true);
    setError("");
    try {
      const token = getToken();

      if (!token) {
        setError("You must be logged in to delete a post.");
        setDeleteLoading(false);
        return;
      }

      await axios.delete(`${API_BASE}/blog/${postId}`, {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setPosts((prev) => prev.filter((p) => p.id !== postId));
      setDeleteConfirm(null);
      setSuccessMsg("Post deleted successfully.");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      console.error("[BlogListPage] handleDelete failed", err);

      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
      } else if (err.response?.status === 403) {
        setError("You are not authorized to delete this post.");
      } else if (err.response?.status === 404) {
        setError("Post not found. It may have already been deleted.");
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        setDeleteConfirm(null);
      } else {
        setError("Failed to delete post. Please try again.");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  // ✅ Helpers
  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const estimateReadTime = (content = "") => {
    const words = content.trim().split(/\s+/).length;
    return `${Math.max(1, Math.round(words / 200))} min read`;
  };

  // ✅ Filter posts
  const filteredPosts = posts.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "published" && post.is_published) ||
      (filterStatus === "draft" && !post.is_published);
    return matchesSearch && matchesStatus;
  });

  const publishedCount = posts.filter((p) => p.is_published).length;
  const draftCount = posts.filter((p) => !p.is_published).length;

  return (
    <>
      <Helmet>
        <title>Blog Management - Revexia</title>
        <meta name="description" content="Manage your Revexia blog posts." />
      </Helmet>

      <div className="blp-page">

        <div className="blp-body">

          {/* ── Page Header ── */}
          <div className="blp-page-header">
            <div className="blp-page-header-left">
              <div className="blp-page-header-icon">
                <FileText size={26} />
              </div>
              <div>
                <h1 className="blp-page-title">Blog Management</h1>
                <p className="blp-page-subtitle">Create, edit, and manage all blog posts</p>
              </div>
            </div>
            <Link to="/blog/create" className="blp-create-btn">
              <Plus size={17} />
              New Post
            </Link>
          </div>

          {/* ── Stat Cards ── */}
          <div className="blp-stats-row">
            <div className="blp-stat-card">
              <p className="blp-stat-value">{posts.length}</p>
              <p className="blp-stat-label">Total Posts</p>
            </div>
            <div className="blp-stat-card blp-stat-green">
              <p className="blp-stat-value">{publishedCount}</p>
              <p className="blp-stat-label">Published</p>
            </div>
            <div className="blp-stat-card blp-stat-yellow">
              <p className="blp-stat-value">{draftCount}</p>
              <p className="blp-stat-label">Drafts</p>
            </div>
          </div>

          {/* ── Alerts ── */}
          {error && (
            <div className="blp-alert blp-alert-error">
              <AlertCircle size={16} /> <span>{error}</span>
            </div>
          )}
          {successMsg && (
            <div className="blp-alert blp-alert-success">
              <CheckCircle2 size={16} /> <span>{successMsg}</span>
            </div>
          )}

          {/* ── Toolbar ── */}
          <div className="blp-toolbar">
            <div className="blp-search-wrapper">
              <Search size={16} className="blp-search-icon" />
              <input
                type="text"
                placeholder="Search posts..."
                className="blp-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="blp-filter-tabs">
              {["all", "published", "draft"].map((status) => (
                <button
                  key={status}
                  className={`blp-filter-tab ${filterStatus === status ? "active" : ""}`}
                  onClick={() => setFilterStatus(status)}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* ── Loading ── */}
          {isLoading && (
            <div className="blp-loading">
              <div className="blp-spinner" />
              <p>Loading posts...</p>
            </div>
          )}

          {/* ── Empty ── */}
          {!isLoading && filteredPosts.length === 0 && (
            <div className="blp-empty">
              <FileText size={48} className="blp-empty-icon" />
              <h3 className="blp-empty-title">
                {searchQuery ? "No posts match your search" : "No posts yet"}
              </h3>
              <p className="blp-empty-text">
                {searchQuery ? "Try a different keyword." : "Create your first blog post to get started."}
              </p>
              {!searchQuery && (
                <Link to="/blog/create" className="blp-create-btn" style={{ marginTop: "16px" }}>
                  <Plus size={16} /> Create First Post
                </Link>
              )}
            </div>
          )}

          {/* ── Posts Table ── */}
          {!isLoading && filteredPosts.length > 0 && (
            <div className="blp-table-card">
              <table className="blp-table">
                <thead>
                  <tr>
                    <th>Post</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Read Time</th>
                    <th className="blp-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map((post) => (
                    <tr key={post.id} className="blp-table-row">

                      {/* Post Info */}
                      <td className="blp-td-post">
                        <div className="blp-post-info">
                          <div className="blp-post-thumb">
                            {post.media_url ? (
                              <img
                                src={post.media_url}
                                alt={post.title}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className="blp-post-thumb-fallback"
                              style={{ display: post.media_url ? "none" : "flex" }}
                            >
                              <FileText size={18} />
                            </div>
                          </div>
                          <div className="blp-post-text">
                            <p className="blp-post-title">{post.title}</p>
                            <p className="blp-post-excerpt">
                              {post.excerpt && post.excerpt !== "string"
                                ? post.excerpt.slice(0, 80) + (post.excerpt.length > 80 ? "..." : "")
                                : post.content?.slice(0, 80) + "..."}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td>
                        <span className={`blp-status-badge ${post.is_published ? "published" : "draft"}`}>
                          {post.is_published ? (
                            <><Eye size={11} /> Published</>
                          ) : (
                            <><EyeOff size={11} /> Draft</>
                          )}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="blp-td-meta">
                        <div className="blp-meta-row">
                          <Calendar size={13} />
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                      </td>

                      {/* Read Time */}
                      <td className="blp-td-meta">
                        <div className="blp-meta-row">
                          <Clock size={13} />
                          <span>{estimateReadTime(post.content)}</span>
                        </div>
                      </td>

                      {/* ✅ Actions */}
                      <td className="blp-td-actions">
                        <div className="blp-actions-group">

                          {/* View */}
                          <Link
                            to={`/blog/${post.id}`}
                            className="blp-action-btn blp-action-view"
                            title="View Post"
                          >
                            <Eye size={15} />
                          </Link>

                          {/* ✅ Edit */}
                          <button
                            className="blp-action-btn blp-action-edit"
                            title="Edit Post"
                            onClick={() => navigate(`/blog/edit/${post.id}`)}
                          >
                            <Edit3 size={15} />
                          </button>

                          {/* Delete */}
                          <button
                            className="blp-action-btn blp-action-delete"
                            title="Delete Post"
                            onClick={() => setDeleteConfirm(post.id)}
                          >
                            <Trash2 size={15} />
                          </button>

                        </div>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>

        {/* ── Delete Confirm Modal ── */}
        {deleteConfirm && (
          <div className="blp-modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="blp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="blp-modal-icon">
                <Trash2 size={28} />
              </div>
              <h3 className="blp-modal-title">Delete Post?</h3>
              <p className="blp-modal-text">
                This action cannot be undone. The post will be permanently deleted.
              </p>
              <div className="blp-modal-actions">
                <button
                  className="blp-modal-cancel"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  className="blp-modal-confirm"
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <span className="blp-spinner-sm" /> : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default BlogListPage;